import React, { useEffect, useRef, useState } from 'react';
import { Icon, Link, Pill, TIcon } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { TCharacteristic } from 'types/UserReviews';

import Classes from './UserReviewsCharacteristics.scss';

const ROW_HEIGHT = 36;

type TUserReviewsCharacteristicsProps = {
  title: string;
  iconName: TIcon;
  characteristics: TCharacteristic[];
  className?: string;
};

const UserReviewsCharacteristics = ({
  title,
  iconName,
  characteristics,
  className,
}: TUserReviewsCharacteristicsProps) => {
  const { t } = useI18nContext();
  const [isOpen, setIsOpen] = useState(false);
  const [firstRowCount, setFirstRowCount] = useState<number | null>(null);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    const measure = () => {
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length === 0) return;

      const firstRowTop = children[0].offsetTop;
      const count = children.filter((child) => child.offsetTop === firstRowTop).length;

      setFirstRowCount(count < children.length ? count : null);
      setFullHeight(el.scrollHeight);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, [characteristics]);

  const handleToggleClick = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const isOverflowing = firstRowCount !== null;
  const visibleCharacteristics =
    !isOpen && isOverflowing ? characteristics.slice(0, firstRowCount - 1) : characteristics;

  const pillNodes = (chars: typeof characteristics) =>
    chars.map((characteristic) => (
      <Pill
        key={characteristic.text}
        counter={characteristic.count}
        translations={{
          ariaLabelActive: characteristic.text,
          ariaLabelDefault: characteristic.text,
        }}
      >
        {characteristic.text}
      </Pill>
    ));

  let containerHeight: number | undefined;
  if (isOverflowing) {
    containerHeight = isOpen ? (fullHeight ?? undefined) : ROW_HEIGHT;
  }

  return (
    <div className={className}>
      <div className={Classes.header}>
        <Icon name={iconName} />
        <p>{title}</p>
      </div>

      <div className={Classes.pillsContainer} style={{ height: containerHeight }}>
        {/* Hidden container with all pills — used only for measuring row breaks */}
        <div ref={measureRef} className={Classes.pillsMeasure} aria-hidden="true">
          {pillNodes(characteristics)}
        </div>

        <div ref={pillsRef} className={Classes.pills}>
          {pillNodes(visibleCharacteristics)}

          {isOverflowing && !isOpen && (
            <Link onClick={handleToggleClick} iconRight="arrow-down-regular">
              {t('userReviews.more')}
            </Link>
          )}

          {isOverflowing && isOpen && (
            <Link onClick={handleToggleClick} iconRight="arrow-up-regular">
              {t('userReviews.less')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserReviewsCharacteristics;
